/**
 * 🛡️ MODERATION DASHBOARD
 * Central hub for managing suspended/blacklisted vehicles, hosts, and drivers
 * Includes supervisor approval workflow for reactivations
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stack,
  Badge,
  Skeleton,
  Paper,
  Divider,
} from '@mui/material';
import {
  Block,
  Refresh,
  CheckCircle,
  Cancel,
  DirectionsCar,
  Person,
  LocalShipping,
  History,
  Info,
  Warning,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import {
  getSuspendedEntities,
  getPendingReactivationRequests,
  getModerationHistory,
  approveReactivation,
  rejectReactivation,
  type ReactivationRequest,
  type SuspendedEntity,
  type ModerationAction,
} from '@/services/moderationService';

export const ModerationDashboard: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reactivationRequests, setReactivationRequests] = useState<ReactivationRequest[]>([]);
  const [suspendedVehicles, setSuspendedVehicles] = useState<any[]>([]);
  const [suspendedHosts, setSuspendedHosts] = useState<any[]>([]);
  const [suspendedDrivers, setSuspendedDrivers] = useState<any[]>([]);
  const [blacklistedEntities, setBlacklistedEntities] = useState<any[]>([]);
  const [moderationHistory, setModerationHistory] = useState<ModerationAction[]>([]);

  // Dialog states
  const [approvalDialog, setApprovalDialog] = useState<{
    open: boolean;
    request: ReactivationRequest | null;
    action: 'approve' | 'reject';
  }>({
    open: false,
    request: null,
    action: 'approve',
  });
  const [approvalNotes, setApprovalNotes] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (selectedTab === 0) {
        // Suspended Vehicles
        const result = await getSuspendedEntities('vehicle', 'suspended');
        if (result.success) setSuspendedVehicles(result.data?.vehicles || []);
      } else if (selectedTab === 1) {
        // Suspended Hosts
        const result = await getSuspendedEntities('host', 'suspended');
        if (result.success) setSuspendedHosts(result.data?.hosts || []);
      } else if (selectedTab === 2) {
        // Suspended Drivers
        const result = await getSuspendedEntities('driver', 'suspended');
        if (result.success) setSuspendedDrivers(result.data?.drivers || []);
      } else if (selectedTab === 3) {
        // Blacklisted Entities
        const result = await getSuspendedEntities('all', 'blacklisted');
        if (result.success) {
          setBlacklistedEntities([
            ...(result.data?.vehicles || []).map((v: any) => ({ ...v, type: 'vehicle' })),
            ...(result.data?.hosts || []).map((h: any) => ({ ...h, type: 'host' })),
            ...(result.data?.drivers || []).map((d: any) => ({ ...d, type: 'driver' })),
          ]);
        }
      } else if (selectedTab === 4) {
        // Reactivation Requests (Supervisor Only)
        const result = await getPendingReactivationRequests();
        if (result.success) setReactivationRequests(result.data || []);
      } else if (selectedTab === 5) {
        // Moderation History
        const result = await getModerationHistory();
        if (result.success) setModerationHistory(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load moderation data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReactivation = async () => {
    if (!approvalDialog.request) return;

    try {
      const result = await approveReactivation(approvalDialog.request.id, approvalNotes);
      if (result.success) {
        toast.success('Reactivation approved successfully!');
        setApprovalDialog({ open: false, request: null, action: 'approve' });
        setApprovalNotes('');
        loadData();
      } else {
        toast.error(result.error || 'Failed to approve reactivation');
      }
    } catch (error) {
      toast.error('Failed to approve reactivation');
    }
  };

  const handleRejectReactivation = async () => {
    if (!approvalDialog.request || !approvalNotes) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      const result = await rejectReactivation(approvalDialog.request.id, approvalNotes);
      if (result.success) {
        toast.success('Reactivation rejected');
        setApprovalDialog({ open: false, request: null, action: 'reject' });
        setApprovalNotes('');
        loadData();
      } else {
        toast.error(result.error || 'Failed to reject reactivation');
      }
    } catch (error) {
      toast.error('Failed to reject reactivation');
    }
  };

  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'vehicle':
        return <DirectionsCar />;
      case 'host':
        return <Person />;
      case 'driver':
        return <LocalShipping />;
      default:
        return <Info />;
    }
  };

  const renderSuspendedVehicles = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Vehicle</TableCell>
            <TableCell>Host</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Suspended At</TableCell>
            <TableCell>Suspended By</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
              </TableRow>
            ))
          ) : suspendedVehicles.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No suspended vehicles
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            suspendedVehicles.map((vehicle) => (
              <TableRow key={vehicle.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {vehicle.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {vehicle.id.substring(0, 8)}...
                  </Typography>
                </TableCell>
                <TableCell>{vehicle.hostName || 'N/A'}</TableCell>
                <TableCell>
                  <Typography variant="body2">{vehicle.suspensionReason}</Typography>
                </TableCell>
                <TableCell>
                  {new Date(vehicle.suspendedAt).toLocaleDateString()}
                </TableCell>
                <TableCell>{vehicle.suspendedBy?.name || 'Admin'}</TableCell>
                <TableCell align="right">
                  {vehicle.canReactivate && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<CheckCircle />}
                    >
                      Request Reactivation
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderReactivationRequests = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Entity</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Justification</TableCell>
            <TableCell>Requested By</TableCell>
            <TableCell>Requested At</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
              </TableRow>
            ))
          ) : reactivationRequests.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No pending reactivation requests
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            reactivationRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {getEntityIcon(request.entityType)}
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {request.entityName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {request.entityId.substring(0, 8)}...
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip label={request.entityType} size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{request.justification}</Typography>
                  {request.notes && (
                    <Typography variant="caption" color="text.secondary">
                      {request.notes}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{request.requestedBy.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {request.requestedBy.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(request.requestedAt).toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() =>
                        setApprovalDialog({ open: true, request, action: 'approve' })
                      }
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() =>
                        setApprovalDialog({ open: true, request, action: 'reject' })
                      }
                    >
                      Reject
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderBlacklistedEntities = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Entity</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Blacklisted At</TableCell>
            <TableCell>Blacklisted By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
              </TableRow>
            ))
          ) : blacklistedEntities.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No blacklisted entities
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            blacklistedEntities.map((entity) => (
              <TableRow key={`${entity.type}-${entity.id}`}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {getEntityIcon(entity.type)}
                    <Typography variant="body2" fontWeight={500}>
                      {entity.name}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip label={entity.type} size="small" color="error" />
                </TableCell>
                <TableCell>{entity.suspensionReason}</TableCell>
                <TableCell>
                  {new Date(entity.suspendedAt).toLocaleDateString()}
                </TableCell>
                <TableCell>{entity.suspendedBy?.name || 'Admin'}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Moderation Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage suspended and blacklisted vehicles, hosts, and drivers
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
          Refresh
        </Button>
      </Box>

      {/* Warning Alert */}
      <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
        <Typography variant="body2" fontWeight={500}>
          ⚠️ Moderation Actions are Permanent
        </Typography>
        <Typography variant="caption">
          Blacklisted entities cannot be reactivated. Suspensions require supervisor approval to reactivate.
        </Typography>
      </Alert>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, val) => setSelectedTab(val)} variant="scrollable">
          <Tab label="Suspended Vehicles" icon={<DirectionsCar />} iconPosition="start" />
          <Tab label="Suspended Hosts" icon={<Person />} iconPosition="start" />
          <Tab label="Suspended Drivers" icon={<LocalShipping />} iconPosition="start" />
          <Tab
            label="Blacklisted Entities"
            icon={<Block />}
            iconPosition="start"
            sx={{ color: 'error.main' }}
          />
          <Tab
            label={
              <Badge badgeContent={reactivationRequests.length} color="warning">
                Reactivation Requests
              </Badge>
            }
            icon={<CheckCircle />}
            iconPosition="start"
          />
          <Tab label="History" icon={<History />} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Content */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {selectedTab === 0 && renderSuspendedVehicles()}
          {selectedTab === 1 && <Typography sx={{ p: 3 }}>Suspended Hosts (Coming Soon)</Typography>}
          {selectedTab === 2 && <Typography sx={{ p: 3 }}>Suspended Drivers (Coming Soon)</Typography>}
          {selectedTab === 3 && renderBlacklistedEntities()}
          {selectedTab === 4 && renderReactivationRequests()}
          {selectedTab === 5 && <Typography sx={{ p: 3 }}>Moderation History (Coming Soon)</Typography>}
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog.open}
        onClose={() => setApprovalDialog({ open: false, request: null, action: 'approve' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {approvalDialog.action === 'approve' ? 'Approve Reactivation' : 'Reject Reactivation'}
        </DialogTitle>
        <DialogContent>
          {approvalDialog.request && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  {approvalDialog.request.entityType}: {approvalDialog.request.entityName}
                </Typography>
                <Typography variant="caption">
                  Requested by: {approvalDialog.request.requestedBy.name}
                </Typography>
              </Alert>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Justification:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {approvalDialog.request.justification}
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label={approvalDialog.action === 'approve' ? 'Notes (Optional)' : 'Rejection Reason (Required)'}
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder={
                  approvalDialog.action === 'approve'
                    ? 'Add any notes about this approval...'
                    : 'Explain why this request is being rejected...'
                }
                required={approvalDialog.action === 'reject'}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApprovalDialog({ open: false, request: null, action: 'approve' })}
          >
            Cancel
          </Button>
          {approvalDialog.action === 'approve' ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleApproveReactivation}
            >
              Approve
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              startIcon={<Cancel />}
              onClick={handleRejectReactivation}
              disabled={!approvalNotes}
            >
              Reject
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModerationDashboard;
